// i18n/en.ts — английский словарь «Упряжка» (i18n baseline).
// Структура ключей идентична ru.ts — те же namespace'ы.
// Поля в прототипе: используются для будущего расширения ru→en переключателя.

import type { Dict } from './ru';

/** Английский словарь en-US. */
export const en: Dict = {
  // common
  'common.appName': 'Upryazhka',
  'common.appSubtitle': 'Corporate portal',
  'common.greeting': 'Hello, {name}!',
  'common.welcome': 'Welcome',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.create': 'Create',
  'common.search': 'Search',
  'common.loading': 'Loading…',
  'common.error': 'Error',
  'common.retry': 'Retry',
  'common.back': 'Back',
  // B2 (pintest-fix-B): fallback for unknown entity id instead of UUID stub.
  'common.unknown': 'Unknown',
  // U-20 (sled-V9 W6): department search hint (DepartmentAutocomplete).
  'common.departmentSearchHint': 'Start typing a department name',
  // SLED-EXAM-FE-V17-W1b: list pagination labels (canon payments/invoices.rowsPerPage / of).
  'common.rowsPerPage': 'Rows per page:',
  'common.of': 'of',
  // F-2 (pintest V15): невалидная дата в полях MUI X (DateTimeField) — error-текст.
  'forms.dateInvalid': 'Invalid date',

  // auth
  'auth.login.title': 'Sign in to Upryazhka',
  'auth.login.subtitle': 'Enter your credentials to sign in',
  'auth.login.username': 'Username',
  'auth.login.password': 'Password',
  'auth.login.submit': 'Sign in',
  'auth.login.error.invalid': 'Invalid credentials',
  'auth.logout': 'Sign out',
  'auth.userMenu.label': 'User menu',

  // nav
  'nav.dashboard': 'Dashboard',
  'nav.tasks': 'Tasks',
  'nav.deals': 'Deals',
  'nav.contacts': 'Contacts',
  'nav.companies': 'Companies',
  'nav.calendar': 'Calendar',
  'nav.admin': 'Admin',

  // user menu (R39b / R40; V16 W11 — admin dictionaries)
  'menu.profile': 'Profile',
  'menu.team': 'Team',
  'menu.admin': 'Administration',

  // topbar (R42 / U-13: user menu, bell stub, account tooltip)
  'topbar.account.tooltip': 'Account: {name}',
  'topbar.notifications.label': 'Notifications',
  'topbar.notifications.tooltip': 'Notifications — coming soon',

  // dashboard
  'dashboard.subtitle': 'Upryazhka system overview',
  'dashboard.kpi.tasks': 'Tasks',
  'dashboard.kpi.deals': 'Deals',
  'dashboard.kpi.eventsToday': 'Events today',
  'dashboard.kpi.contacts': 'Contacts',
  'dashboard.recentActivity': 'Recent activity',
  'dashboard.adminNote': 'You are signed in as administrator. Audit-events API and extended actions are available.',
  // U-25 (sled-V9): Dashboard widget SectionHeader — titles and action.
  'dashboard.auditLogTitle': 'Audit log',
  'dashboard.auditLogAll': 'All events',
  'dashboard.recentActivityTitle': 'Recent activity',

  // tasks
  'tasks.title': 'Tasks',
  'tasks.subtitle': 'Manage your department tasks',
  'tasks.create': 'Create task',
  'tasks.empty': 'No tasks',
  'tasks.status.NEW': 'New',
  'tasks.status.IN_PROGRESS': 'In progress',
  'tasks.status.BLOCKED': 'Blocked',
  'tasks.status.DONE': 'Done',
  'tasks.status.CANCELLED': 'Cancelled',
  'tasks.status.noTransitions': 'No transitions available for this status',
  'tasks.operationError': 'Failed to perform the operation',
  // R41 / U-16: task priorities (backend TaskPriority).
  'tasks.priority.LOW': 'Low',
  'tasks.priority.MEDIUM': 'Medium',
  'tasks.priority.HIGH': 'High',
  'tasks.priority.CRITICAL': 'Critical',
  // V21 W2: диалог смены приоритета (TaskDetailPage).
  'tasks.changePriority': 'Change priority',
  'tasks.priorityLabel': 'New priority',
  // R38b / U-9: task ↔ deal link
  'tasks.deal': 'Deal',
  'tasks.linkedDeal': 'Linked deal',
  // FIX-C (pintest S4): related deal removed/canceled (backend → NotFound).
  'tasks.dealDeleted': 'Deal deleted',
  // B1 (pintest-fix-B): required department in the task create form.
  'tasks.department': 'Department',
  'tasks.departmentRequired': 'Select a department',
  // U-21 (sled-V9): task due date — must not be in the past (zod refine, ISO Instant).
  'tasks.dueAtPast': 'Due date cannot be in the past',
  // U-23 (sled-V9): sortable task list columns.
  'tasks.list.createdAt': 'Created',
  'tasks.list.dueAt': 'Due',

  // crm
  'crm.deals.title': 'Deals',
  'crm.deals.subtitle': 'Manage CRM deals',
  'crm.deals.create': 'Create deal',
  'crm.deals.department': 'Department',
  'crm.deals.department.required': 'Select a department',
  // U-17 (sled-V9): deal form validation — date/amount.
  'crm.deals.dueDatePast': 'Date cannot be in the past',
  'crm.deals.amountInvalid': 'Enter a numeric amount',
  'crm.deals.amountMin': 'Amount must be ≥ 0',
  // U-32 (sled-V11): deal amount — empty/<=0 separate messages (amountSchema in lib/money.ts).
  'crm.deals.amountRequired': 'Enter amount',
  'crm.deals.amountPositive': 'Amount must be greater than 0',
  'crm.deals.createError': 'Failed to create deal',
  'crm.deals.edit.title': 'Edit deal',
  'crm.deals.edit.conflict': 'The data was changed by another user. Refresh the page and try again.',
  // U-28 (sled-V10 W2): assigned-contact suffix in deal edit dialog options.
  'crm.deals.edit.assignedContactSuffix': 'assigned',
  'crm.deals.editError': 'Failed to save deal',
  'crm.deals.nextContact': 'Schedule next contact',
  // U-18 (sled-V9): contact window on deal card — block labels.
  'crm.deals.nextContactLabel': 'Next contact',
  'crm.deals.lastContactLabel': 'Last contact',

  // dealDateField (U-27 sled-V10): date range field toggle segments in the deals filter.
  'dealDateField.createdAt': 'Created',
  'dealDateField.nextContactAt': 'Next contact',
  'dealDateField.dueDate': 'Planned due date',
  // R41 / U-16: deal stages (backend DealStage).
  'crm.deals.stage.NEW': 'New',
  'crm.deals.stage.QUALIFIED': 'Qualified',
  'crm.deals.stage.NEGOTIATION': 'Negotiation',
  'crm.deals.stage.PROPOSAL': 'Proposal',
  'crm.deals.stage.WON': 'Won',
  'crm.deals.stage.LOST': 'Lost',
  // U-19 (sled-V9 W5): DealStagePath — aria-подпись кнопки стадии пути.
  'crm.deals.stagePath.aria': 'Stage {stage}',
  // V16 W3: lost-reason dialog when moving a deal to LOST.
  'crm.deals.lostReason.title': 'Loss reason',
  'crm.deals.lostReason.label': 'Loss reason',
  'crm.deals.lostReason.confirm': 'Confirm',
  'crm.deals.lostReason.loadError': 'Failed to load rejection reasons',
  'crm.deals.rejectionReason': 'Loss reason',
  // R38b / U-9: deal card «Tasks» tab
  'crm.deals.tasksTab': 'Tasks',
  'crm.deals.createTask': 'Create task',
  'crm.deals.noTasks': 'No tasks linked to this deal',
  'crm.contacts.title': 'Contacts',
  'crm.companies.title': 'Companies',
  // U-23 (sled-V9): sortable "Created" column in companies/contacts lists.
  'crm.companies.list.createdAt': 'Created',
  'crm.contacts.list.createdAt': 'Created',
  // U-22 (sled-V9): soft website validation — scheme optional, TLD required.
  'crm.companies.websiteHint': 'https:// can be omitted — we will add it',
  'crm.companies.websiteInvalid': 'Website must be a domain or URL (scheme optional)',

  // calendar
  'calendar.title': 'Calendar',
  'calendar.subtitle': 'Events calendar',
  'calendar.create': 'Create event',
  'calendar.empty': 'No events in selected range',
  'calendar.view.month': 'Month',
  'calendar.view.week': 'Week',
  'calendar.view.day': 'Day',
  // B1 (pintest-fix-B): required department in the calendar create form.
  'calendar.department': 'Department',
  'calendar.departmentRequired': 'Select a department',
  // U-30 (sled-V10 W1): real error cause + cross-field endAt > startAt.
  'calendar.createError': 'Failed to create event',
  'calendar.endAtAfterStart': 'End date must be later than start date',
  // R41 / U-16: calendar event types (backend CalendarType).
  'calendar.type.MEETING': 'Meeting',
  'calendar.type.CALL': 'Call',
  'calendar.type.TASK': 'Task',
  'calendar.type.REMINDER': 'Reminder',
  // R41 / U-16: attendee statuses (backend AttendeeStatus).
  'calendar.attendee.PENDING': 'Pending',
  'calendar.attendee.ACCEPTED': 'Accepted',
  'calendar.attendee.DECLINED': 'Declined',
  'calendar.attendee.TENTATIVE': 'Tentative',

  // R41 / U-16: audit event types (backend AuditEventType, 46 values).
  'audit.eventType.USER_CREATED': 'User created',
  'audit.eventType.USER_UPDATED': 'User updated',
  'audit.eventType.USER_DELETED': 'User deleted',
  'audit.eventType.USER_LOGIN_SUCCESS': 'Sign-in succeeded',
  'audit.eventType.USER_LOGIN_FAILED': 'Sign-in failed',
  'audit.eventType.USER_LOGOUT': 'Sign-out',
  'audit.eventType.USER_PASSWORD_CHANGED': 'Password changed',
  'audit.eventType.ROLE_ASSIGNED': 'Role assigned',
  'audit.eventType.ROLE_UNASSIGNED': 'Role unassigned',
  'audit.eventType.DEPARTMENT_CREATED': 'Department created',
  'audit.eventType.DEPARTMENT_UPDATED': 'Department updated',
  'audit.eventType.DEPARTMENT_DELETED': 'Department deleted',
  'audit.eventType.USER_DEPARTMENT_ASSIGNED': 'User added to department',
  'audit.eventType.USER_DEPARTMENT_UPDATED': 'User department updated',
  'audit.eventType.USER_DEPARTMENT_REMOVED': 'User removed from department',
  'audit.eventType.TOKEN_REFRESHED': 'Token refreshed',
  'audit.eventType.TOKEN_REFRESH_FAILED': 'Token refresh failed',
  'audit.eventType.TASK_CREATED': 'Task created',
  'audit.eventType.TASK_UPDATED': 'Task updated',
  'audit.eventType.TASK_DELETED': 'Task deleted',
  'audit.eventType.TASK_ASSIGNED': 'Task assigned',
  'audit.eventType.TASK_UNASSIGNED': 'Task unassigned',
  'audit.eventType.TASK_STATUS_CHANGED': 'Task status changed',
  'audit.eventType.TASK_ACCESS_DENIED_CROSS_DEPARTMENT': 'Task access denied (cross-department)',
  'audit.eventType.DEAL_CREATED': 'Deal created',
  'audit.eventType.DEAL_UPDATED': 'Deal updated',
  'audit.eventType.DEAL_DELETED': 'Deal deleted',
  'audit.eventType.DEAL_STAGE_CHANGED': 'Deal stage changed',
  'audit.eventType.DEAL_ASSIGNED': 'Deal owner changed',
  'audit.eventType.DEAL_COMMENT_ADDED': 'Deal comment added',
  'audit.eventType.DEAL_ACCESS_DENIED_CROSS_DEPARTMENT': 'Deal access denied (cross-department)',
  'audit.eventType.CONTACT_CREATED': 'Contact created',
  'audit.eventType.CONTACT_UPDATED': 'Contact updated',
  'audit.eventType.CONTACT_DELETED': 'Contact deleted',
  'audit.eventType.CONTACT_ACCESS_DENIED_CROSS_DEPARTMENT': 'Contact access denied (cross-department)',
  'audit.eventType.COMPANY_CREATED': 'Company created',
  'audit.eventType.COMPANY_UPDATED': 'Company updated',
  'audit.eventType.COMPANY_DELETED': 'Company deleted',
  'audit.eventType.COMPANY_ACCESS_DENIED_CROSS_DEPARTMENT': 'Company access denied (cross-department)',
  'audit.eventType.CALENDAR_EVENT_CREATED': 'Event created',
  'audit.eventType.CALENDAR_EVENT_UPDATED': 'Event updated',
  'audit.eventType.CALENDAR_EVENT_DELETED': 'Event deleted',
  'audit.eventType.CALENDAR_EVENT_ATTENDEE_ADDED': 'Attendee added',
  'audit.eventType.CALENDAR_EVENT_ATTENDEE_REMOVED': 'Attendee removed',
  'audit.eventType.CALENDAR_EVENT_ATTENDEE_RESPONDED': 'Attendee responded to invitation',
  'audit.eventType.CALENDAR_EVENT_ACCESS_DENIED_CROSS_DEPARTMENT': 'Event access denied (cross-department)',
  // V12 U-34/U-35: lead and payment events (U-16 micro-fix — audit type localization).
  'audit.eventType.LEAD_CREATED': 'Lead created',
  'audit.eventType.LEAD_UPDATED': 'Lead updated',
  'audit.eventType.LEAD_STATUS_CHANGED': 'Lead status changed',
  'audit.eventType.PAYMENT_CREATED': 'Payment created',
  'audit.eventType.PAYMENT_UPDATED': 'Payment updated',
  // V14 W6 (§3.2.1): invoice audit events (INVOICE_CREATED/UPDATED/STATUS_CHANGED).
  'audit.eventType.INVOICE_CREATED': 'Invoice created',
  'audit.eventType.INVOICE_UPDATED': 'Invoice updated',
  'audit.eventType.INVOICE_STATUS_CHANGED': 'Invoice status changed',
  // V15: email delivery audit event (EMAIL_DELIVERY_CREATED).
  'audit.eventType.EMAIL_DELIVERY_CREATED': 'Email created',
  // R41 / U-16: audit entity types (lowercase entityType on the wire).
  'audit.entity.USER': 'User',
  'audit.entity.DEPARTMENT': 'Department',
  'audit.entity.ROLE': 'Role',
  'audit.entity.TASK': 'Task',
  'audit.entity.DEAL': 'Deal',
  'audit.entity.CONTACT': 'Contact',
  'audit.entity.COMPANY': 'Company',
  'audit.entity.CALENDAR_EVENT': 'Event',
  'audit.entity.LEAD': 'Lead',
  'audit.entity.PAYMENT': 'Payment',
  'audit.entity.INVOICE': 'Invoice',
  'audit.entity.EMAIL_DELIVERY': 'Email',

  // profile (R39b / U-10)
  'profile.title': 'My profile',
  'profile.subtitle': 'Personal data and contacts',
  'profile.username': 'Username',
  'profile.email': 'Email',
  'profile.displayName': 'Display name',
  'profile.phone': 'Phone',
  'profile.position': 'Position',
  'profile.description': 'About me',
  'profile.saveSuccess': 'Profile updated',
  'profile.saveError': 'Failed to update profile',

  // team (R40 / U-11)
  'team.title': 'Team',
  'team.subtitle': 'Manage portal users (admin only)',
  'team.create': 'Create user',
  'team.createSubmit': 'Create',
  'team.createTitle': 'New user',
  'team.createError': 'Failed to create user',
  'team.operationError': 'Failed to change user status',
  'team.loadError': 'Failed to load users',
  'team.loadErrorDesc': 'Check your connection and try again',
  'team.empty': 'No users',
  'team.emptyDesc': 'Create the first user with the button above',
  'team.columns.user': 'User',
  'team.columns.email': 'Email',
  'team.columns.phone': 'Phone',
  'team.columns.position': 'Position',
  'team.columns.roles': 'Roles',
  'team.columns.status': 'Status',
  'team.columns.actions': 'Actions',
  'team.activate': 'Activate',
  'team.deactivate': 'Deactivate',
  'team.form.username': 'Username',
  'team.form.email': 'Email',
  'team.form.password': 'Password',
  'team.form.displayName': 'Display name',
  'users.status.active': 'Active',
  'users.status.locked': 'Locked',
  'users.status.disabled': 'Disabled',

  // dict (V16 W11): admin dictionaries for reasons and currencies
  'dict.title': 'Administration',
  'dict.subtitle': 'Rejection reasons and currencies',
  'dict.tabs.reasons': 'Reasons',
  'dict.tabs.currencies': 'Currencies',
  'dict.reasons.create': 'Add reason',
  'dict.reasons.createTitle': 'New reason',
  'dict.reasons.editTitle': 'Edit reason',
  'dict.reasons.name': 'Name',
  'dict.reasons.empty': 'No reasons',
  'dict.reasons.emptyDesc': 'Add the first reason with the button above',
  'dict.reasons.loadError': 'Failed to load reasons',
  'dict.reasons.loadErrorDesc': 'Check your connection and try again',
  'dict.reasons.saveError': 'Failed to save reason',
  'dict.reasons.deleteError': 'Failed to delete reason',
  'dict.currencies.create': 'Add currency',
  'dict.currencies.createTitle': 'New currency',
  'dict.currencies.editTitle': 'Edit currency',
  'dict.currencies.code': 'Code',
  'dict.currencies.symbol': 'Symbol',
  'dict.currencies.empty': 'No currencies',
  'dict.currencies.emptyDesc': 'Add the first currency with the button above',
  'dict.currencies.loadError': 'Failed to load currencies',
  'dict.currencies.loadErrorDesc': 'Check your connection and try again',
  'dict.currencies.saveError': 'Failed to save currency',
  'dict.currencies.deleteError': 'Failed to delete currency',
  'dict.columns.status': 'Status',
  'dict.columns.actions': 'Actions',
  'dict.status.active': 'Active',
  'dict.status.inactive': 'Inactive',
  'dict.activate': 'Activate',
  'dict.deactivate': 'Deactivate',

  // search (R63 / U-34-D): search bar with field flags (SearchFieldBar).
  'search.fields.label': 'Fields',
  'search.reset': 'Reset',
  'search.placeholder': 'Search…',
  'search.field.name': 'Name',
  'search.field.website': 'Website',
  'search.field.site_name': 'Site name',
  'search.field.email': 'Email',
  'search.field.phone': 'Phone',
  'search.field.tax_id': 'Tax ID',
  'search.field.license_number': 'License number',
  'search.field.ip_address': 'IP address',
  'search.field.first_name': 'First name',
  'search.field.last_name': 'Last name',
  'search.field.company_id': 'Company',
  'search.field.company_name': 'Company',
  // V13 U-38 (W4): deal search fields (DealSearchField) — RU/EN parity.
  'search.field.title': 'Title',
  'search.field.description': 'Description',
  'search.field.stage': 'Stage',
  'search.field.currency': 'Currency',
  'search.field.contact_id': 'Contact',
  'search.field.owner_id': 'Owner',

  // leads (R62 / V12 U-34-E): lead pages.
  'leads.title': 'Leads',
  'leads.subtitle': 'Inbound requests and manual leads',
  'leads.loading': 'Loading leads…',
  'leads.create': 'Create lead',
  'leads.create.dialogTitle': 'Create lead',
  'leads.create.submit': 'Create',
  'leads.create.error': 'Failed to create lead',
  'leads.empty': 'No leads',
  'leads.emptyDesc': 'Create the first lead',
  'leads.search': 'Search leads',
  'leads.loadError': 'Failed to load leads',
  'leads.detail.notFound': 'Lead not found',
  'leads.edit.dialogTitle': 'Edit lead',
  'leads.edit.error': 'Failed to save lead',
  // V16 W5: discard-reason dialog when entering DISCARDED.
  'leads.discardReason.title': 'Discard reason',
  'leads.discardReason.label': 'Discard reason',
  'leads.discardReason.confirm': 'Confirm',
  'leads.discardReason.loadError': 'Failed to load rejection reasons',
  'leads.rejectionReason': 'Rejection reason',
  'leads.convert.dialogTitle': 'Convert to deal',
  'leads.convert.submit': 'Convert to deal',
  'leads.convert.error': 'Failed to convert lead',
  // U-16: lead statuses and sources — i18n (no raw enums in UI).
  'leads.status.NEW': 'New',
  'leads.status.IN_PROGRESS': 'In progress',
  'leads.status.CONVERTED': 'Converted',
  'leads.status.DISCARDED': 'Discarded',
  'leads.source.LANDING': 'Landing',
  'leads.source.MANUAL': 'Manual',
  'leads.source.JIVO': 'Jivo (chat)',

  // payments (V12 U-35, wave R66-R67): payments section + entity block.
  'payments.title': 'Payments',
  'payments.subtitle': 'Payments and fiscal receipts',
  'payments.loading': 'Loading payments…',
  'payments.loadError': 'Failed to load payments',
  'payments.create': 'Create payment',
  'payments.create.dialogTitle': 'Create payment',
  'payments.create.submit': 'Create',
  'payments.create.error': 'Failed to create payment',
  'payments.amount': 'Amount',
  'payments.currency': 'Currency',
  'payments.paymentDate': 'Payment date',
  'payments.fiscalDocNumber': 'Fiscal №',
  'payments.source': 'Source',
  'payments.status': 'Status',
  'payments.entity': 'Binding',
  'payments.entityType.label': 'Binding type',
  'payments.actions': 'Actions',
  'payments.rowsPerPage': 'Per page:',
  'payments.of': 'of',
  'payments.empty': 'No payments',
  'payments.emptyDesc': 'Create a payment or upload a receipt',
  'payments.upload': 'Upload receipt',
  'payments.upload.dialogTitle': 'Upload receipt',
  'payments.upload.submit': 'Upload',
  'payments.upload.error': 'Failed to upload receipt',
  'payments.upload.file': 'Receipt file',
  'payments.upload.maxSize': 'File is larger than 10 MB',
  'payments.upload.success': 'Payment recorded (QR)',
  'payments.upload.createManual': 'Create manually',
  'payments.update.error': 'Failed to change payment status',
  'payments.confirm': 'Confirm',
  'payments.reject': 'Reject',
  'payments.block.title': 'Payments',
  'payments.block.add': '+ Payment',
  'payments.block.showAll': 'Show all →',
  'payments.status.PENDING': 'Pending',
  'payments.status.CONFIRMED': 'Confirmed',
  'payments.status.FAILED': 'Failed',
  'payments.source.QR': 'QR',
  'payments.source.MANUAL': 'Manual',
  'payments.entityType.COMPANY': 'Company',
  'payments.entityType.CONTACT': 'Contact',
  'payments.entityType.DEAL': 'Deal',
  'payments.entityType.LEAD': 'Lead',
  // V14 W7 (§3.7/§3.8): invoice (column/chip/dialog), search by № (NUMBER), sort, «Pay invoice».
  'payments.invoice': 'Invoice',
  'payments.invoiceNumber': 'Invoice №',
  'payments.payForInvoice': 'Pay invoice',
  'payments.paymentNumber': 'Payment №',
  'payments.search': 'Search by number',
  'payments.sort': 'Sort',
  'payments.sort.createdAt': 'Created',

  // invoices (V14 W6, §3.8): Invoices section + deal-card block.
  'invoices.title': 'Invoices',
  'invoices.subtitle': 'Invoices issued against deals',
  'invoices.loading': 'Loading invoices…',
  'invoices.loadError': 'Failed to load invoices',
  'invoices.empty': 'No invoices',
  'invoices.emptyDesc': 'Create an invoice for a deal',
  'invoices.create': 'Create invoice',
  'invoices.create.dialogTitle': 'New invoice',
  'invoices.create.submit': 'Create',
  'invoices.create.error': 'Failed to create invoice',
  'invoices.create.amountHint': 'Empty — deal amount',
  'invoices.edit.title': 'Edit invoice',
  'invoices.edit.save': 'Save',
  'invoices.edit.issue': 'Issue invoice',
  'invoices.edit.error': 'Failed to save invoice',
  // V15-a §3.8: due date can only be changed (PATCH contract: null = "keep current").
  'invoices.edit.dueDateHint': 'The due date can only be changed: an empty value keeps the current due date',
  'invoices.search': 'Search by number',
  'invoices.deal': 'Deal',
  'invoices.amount': 'Amount',
  'invoices.currency': 'Currency',
  'invoices.invoiceDate': 'Invoice date',
  'invoices.dueDate': 'Due date',
  'invoices.paidAmount': 'Paid',
  'invoices.remaining': 'Remaining',
  'invoices.status': 'Status',
  'invoices.actions': 'Actions',
  'invoices.rowsPerPage': 'Per page:',
  'invoices.of': 'of',
  'invoices.issue': 'Issue',
  'invoices.void': 'Void',
  'invoices.pay': 'Pay',
  'invoices.editAction': 'Edit',
  'invoices.amountInvalid': 'Enter amount as a number ≥ 0',
  // U-16: invoice statuses (backend InvoiceStatus, <domain>.<field>.<VALUE> convention).
  'invoices.status.DRAFT': 'Draft',
  'invoices.status.OPEN': 'Open',
  'invoices.status.PARTIAL': 'Partially paid',
  'invoices.status.PAID': 'Paid',
  'invoices.status.OVERDUE': 'Overdue',
  'invoices.status.VOID': 'Void',
  'invoices.block.title': 'Invoices',
  'invoices.block.add': '+ Invoice',
  'invoices.block.showAll': 'Show all →',
  'invoices.block.total': 'Total',
  'invoices.block.paid': 'Paid',
  'invoices.block.remaining': 'Remaining',
  'invoices.block.createPayment': 'Create payment',

  // reports (V14 W10, ТЗ §3.6): Reports page + tables R1..R6 + XLSX export.
  // Stages/sources/statuses — reuse crm.deals.stage.*, leads.source.*, payments.status.*.
  'reports.title': 'Reports',
  'reports.subtitle': 'Sales analytics: 7 reports with XLSX export',
  'reports.loading': 'Loading report…',
  'reports.loadError': 'Failed to load report',
  'reports.empty': 'No data for the period',
  'reports.emptyDesc': 'Adjust the period or filters',
  'reports.inactiveDays': 'Inactive days',
  'reports.truncated': 'Report truncated to 5,000 rows — use filters',
  'reports.download.xlsx': 'Export XLSX',
  'reports.download.error': 'Failed to download the file',
  'reports.truncatedXlsx': '⚠ Report truncated to 5,000 rows — use filters',
  'reports.period.from': 'From date',
  'reports.period.to': 'To date',
  'reports.department': 'Department',
  'reports.department.all': 'All departments',
  // table columns (ReportTable)
  'reports.currency': 'Currency',
  'reports.count': 'Count',
  'reports.amount': 'Amount',
  'reports.stage': 'Stage',
  'reports.dealCount': 'Deals',
  'reports.leadSource': 'Source',
  'reports.totalCount': 'Total leads',
  'reports.convertedCount': 'Converted',
  'reports.conversionPct': 'Conversion, %',
  'reports.owner': 'Manager',
  'reports.activeAmount': 'Active amount',
  'reports.wonCount': 'Won',
  'reports.wonAmount': 'Won amount',
  'reports.month': 'Month',
  'reports.dealsCreated': 'Deals created',
  'reports.dealsCreatedAmount': 'Created amount',
  'reports.paymentsCount': 'Payments',
  'reports.paymentsAmount': 'Payments amount',
  'reports.deal': 'Deal',
  'reports.nextContact': 'Next contact',
  'reports.lastContact': 'Last contact',
  'reports.riskReason': 'Risk reason',
  'reports.sharePct': 'Share, %',
  'reports.totals': 'Totals',
  'reports.reasonName': 'Reason',
  'reports.leadCount': 'Leads',
  'reports.total': 'Total',
  // reports catalog (REPORTS titleKey/descriptionKey)
  'reports.report.funnel.title': 'Funnel by stage',
  'reports.report.funnel.description': 'Active deals by stage and currency (as-of)',
  'reports.report.lead-conversion.title': 'Lead conversion',
  'reports.report.lead-conversion.description': 'Lead-to-deal conversion by source',
  'reports.report.revenue.title': 'Revenue',
  'reports.report.revenue.description': 'Actual (payments + won deals) and forecast by stage',
  'reports.report.revenue.sections.payments': 'Paid (actual)',
  'reports.report.revenue.sections.wonDeals': 'Won deals (actual)',
  'reports.report.revenue.sections.forecast': 'Forecast',
  'reports.report.by-managers.title': 'Deals by managers',
  'reports.report.by-managers.description': 'Deals and amounts by manager and department',
  'reports.report.monthly-dynamics.title': 'Monthly dynamics',
  'reports.report.monthly-dynamics.description': 'Created deals, WON and payments by month',
  'reports.report.risks.title': 'Risks',
  'reports.report.risks.description': 'Deals without contact, with overdue contact or stale',
  'reports.report.refusals.title': 'Refusals',
  'reports.report.refusals.description': 'Refusal reasons: LOST deals and DISCARDED leads in the period',
  // risk reasons (RiskReason: NO_NEXT_CONTACT/OVERDUE_NEXT_CONTACT/STALE)
  'reports.riskReason.NO_NEXT_CONTACT': 'No upcoming contact planned',
  'reports.riskReason.OVERDUE_NEXT_CONTACT': 'Next contact overdue',
  'reports.riskReason.STALE': 'No activity',

  // V15 W7: email (ТЗ §3.8; U-16 паритет — каждому ru-ключу соответствует en-ключ).
  'email.title': 'Email',
  'email.subtitle': 'Accounts, email templates and delivery history',
  'email.tabs.accounts': 'Accounts',
  'email.tabs.templates': 'Templates',
  'email.tabs.deliveries': 'History',
  'email.empty.title': 'Email is not configured',
  'email.empty.desc': 'Connect an SMTP account or Gmail to send invoices by email',
  'email.empty.addSmtp': 'Add SMTP account',
  'email.empty.connectGmail': 'Connect Gmail',
  'email.accounts.checkConnection': 'Check connection',
  'email.accounts.active': 'Active',
  'email.accounts.activate': 'Activate',
  'email.accounts.delete': 'Delete',
  'email.accounts.smtpTitle': 'SMTP account',
  'email.accounts.gmailConnected': 'Gmail connected',
  'email.accounts.gmailError': 'Gmail connection error',
  'email.accounts.disconnectGmail': 'Disconnect Gmail',
  'email.accounts.gmailDisconnected': 'Gmail disconnected',
  'email.smtp.title': 'SMTP settings',
  'email.smtp.fromEmail': 'From email',
  'email.smtp.displayName': 'Display name',
  'email.smtp.host': 'SMTP host',
  'email.smtp.port': 'Port',
  'email.smtp.security': 'Security',
  'email.smtp.security.SSL': 'SSL (465)',
  'email.smtp.security.STARTTLS': 'STARTTLS (587)',
  'email.smtp.username': 'Username',
  'email.smtp.password': 'App password',
  'email.smtp.passwordHint': 'Password is saved; leave empty to keep it',
  'email.smtp.save': 'Save',
  'email.smtp.error': 'Failed to save SMTP account',
  'email.templates.empty': 'No templates',
  'email.templates.emptyDesc': 'Create an email template for invoices',
  'email.templates.create': 'New template',
  'email.templates.systemBadge': 'System',
  'email.templates.loadError': 'Failed to load templates',
  'email.templates.dialog.title': 'Email template',
  'email.templates.name': 'Name',
  'email.templates.subject': 'Subject',
  'email.templates.body': 'Body',
  'email.templates.contentType': 'Content type',
  'email.templates.contentType.HTML': 'HTML',
  'email.templates.contentType.PLAIN': 'Plain text',
  'email.templates.save': 'Save',
  'email.templates.saveError': 'Failed to save template',
  'email.templates.variables': 'Variables',
  'email.templates.preview': 'Preview',
  'email.templates.previewError': 'Failed to render preview',
  'email.templates.invoice': 'Invoice for preview',
  'email.templates.needInvoiceHint': 'Select an invoice…',
  'email.deliveries.empty': 'No deliveries yet',
  'email.deliveries.emptyDesc': 'History appears after the first invoice email',
  'email.deliveries.loadError': 'Failed to load delivery history',
  'email.deliveries.date': 'Date',
  'email.deliveries.recipient': 'Recipient',
  'email.deliveries.subject': 'Subject',
  'email.deliveries.error': 'Error',
  'email.deliveries.status.SENT': 'Sent',
  'email.deliveries.status.FAILED': 'Failed',
  'email.deliveries.status.SKIPPED_NO_ACCOUNT': 'No account',
  'email.deliveries.status.SKIPPED_NO_RECIPIENT': 'No recipient',
  'email.deliveries.status.SKIPPED_RATE_LIMIT': 'Rate limited',
  'email.send.title': 'Send email',
  'email.send.template': 'Template',
  'email.send.account': 'Account',
  'email.send.preview': 'Preview',
  'email.send.submit': 'Send',
  'email.send.success': 'Email sent',
  'email.send.failed': 'Email not sent: {error}',
  'email.send.error': 'Failed to send email',
  'email.send.notConfigured': 'Set up email to send invoices',
  'email.send.noTemplates': 'No email templates — create one in email settings',
  'email.send.noRecipientDeal': 'The deal contact has no email — add it on the contact card',
  'email.send.noRecipientLead': 'The lead has no email — add it on the lead card',
  'email.send.recipient': 'Recipient',
  'email.send.to': 'To',
  'email.send.toHint': 'Client address, not the SMTP account',
  'email.send.subject': 'Subject',
  'email.send.previewRendered': 'Rendered',
  'email.send.previewHtml': 'HTML',
  'email.send.recipientMissing': 'Recipient email is not set',
  'email.nav.label': 'Email',
};
